import { createElement, useCallback, useState } from 'react'

import { useConnect } from 'redux-bundler-hook'

import useDefaultAnnotationFormState from '~/src/Annotations/utils/useDefaultAnnotationFormState'
import { TARGET_RANGES } from '~/src/DataType/TargetRange/constants'
import { FOUND_DEVICES_DIALOG_URL } from '~/src/Device/bundle/found'
import { Allowed, allowedProps, pickAllowedProps } from '~/src/Flags'
import {
  DRYING_FLOW,
  DRYING_FLOW_ROUTE,
  HARVEST_BASED_DRYING_FLOW_ROUTE,
  HARVEST_BASED_HARVESTING_FLOW_ROUTE,
  HARVEST_BASED_LOT_TO_PLANT_FLOW_ROUTE,
  HARVEST_BASED_PROCESSING_FLOW_ROUTE,
  HARVEST_BASED_VEG_TO_FLOWER_FLOW_ROUTE,
  HARVESTING_FLOW,
  LOT_TO_PLANT_FLOW,
  LOT_TO_PLANT_FLOW_ROUTE,
  PROCESSING_FLOW,
  PROCESSING_FLOW_ROUTE,
  VEG_TO_FLOWER_FLOW,
  VEG_TO_FLOWER_FLOW_ROUTE,
} from '~/src/Flow/constants'
import { urls as harvestUrls } from '~/src/Harvest/bundle'
import { REPLACE_PLANT, REPLACE_PLANT_ROUTE } from '~/src/Inventory/constants'
import createLogger from '~/src/Lib/Logging'
import {
  defer,
  EMPTY_OBJECT,
  getDateTime,
  lazyWithRetries,
  throttle,
} from '~/src/Lib/Utils'
import { SUBTRATE_SETTINGS } from '~/src/Room/SubstrateSettings/constants'
import { TASK_EDITOR, TASK_EDITOR_ROUTE } from '~/src/Task/TaskManager/constants'
import { TASK_VIEW_TABS } from '~/src/Task/View/utils'

import Forbidden from '../Routes/Forbidden'
import NotFound from '../Routes/NotFound'

const HarvestView = lazyWithRetries(() => import('~/src/Annotations/HarvestView'))
const ManualReadingForm = lazyWithRetries(() => import('~/src/Annotations/ReadingForm'))
const ConfirmOptionDialog = lazyWithRetries(() => import('~/src/Compliance/Inbox/ConfirmOptionDialog'))
const CultivarsForm = lazyWithRetries(() => import('~/src/Cultivars/Form'))
const TargetRangeDelete = lazyWithRetries(() => import('~/src/DataType/TargetRange/Delete'))
const DayNightTargetRangeView = lazyWithRetries(() => import('~/src/DataType/TargetRange/View/DayNightView'))
const DayNightTargetRangesDialog = lazyWithRetries(() => import('~/src/DataType/TargetRange/DayNightDialog'))
const DeviceActivation = lazyWithRetries(() => import('~/src/Device/ActivationDialog'))
const DeviceSearch = lazyWithRetries(() => import('~/src/Device/ActivationDialog/Search'))
const FacilityForm = lazyWithRetries(() => import('~/src/Facility/Form'))
const FlowDiscardRoute = lazyWithRetries(() => import('~/src/Flow/Discard/Route'))
const HarvestingFlow = lazyWithRetries(() => import('~/src/Flow/Harvesting'))
const ProcessingFlow = lazyWithRetries(() => import('~/src/Flow/Processing'))
const LotToPlant = lazyWithRetries(() => import('~/src/Harvest/Transition/LotToPlant'))
const PhaseAdvance = lazyWithRetries(() => import('~/src/Harvest/Transition/PhaseAdvance'))
const PhaseDelay = lazyWithRetries(() => import('~/src/Harvest/Transition/PhaseDelay'))
const ReplacePlant = lazyWithRetries(() => import('~/src/Inventory/ReplacePlant'))
const InventoryModal = lazyWithRetries(() => import('~/src/Inventory/Modal'))
const ReplaceInvalidTag = lazyWithRetries(() => import('~/src/Inventory/ReplaceInvalidTag'))
const PesticideApplicationDelete = lazyWithRetries(() => import('~/src/IPM/Application/Delete'))
const PesticideApplicationForm = lazyWithRetries(() => import('~/src/IPM/Application/Form'))
const PesticideApplicationView = lazyWithRetries(() => import('~/src/IPM/Application/View'))
const PesticideDelete = lazyWithRetries(() => import('~/src/IPM/Pesticide/Delete'))
const PesticideForm = lazyWithRetries(() => import('~/src/IPM/Pesticide/FormDialog'))
const IrrigationControllerDialog = lazyWithRetries(() => import('~/src/Irrigation/ControllerDialog'))
const IrrigationControllerDelete = lazyWithRetries(() => import('~/src/Irrigation/ControllerDialog/Delete'))
const ModbusRegistersDialog = lazyWithRetries(() => import('~/src/Irrigation/ModbusRegistersDialog'))
const IrrigationScheduleDelete = lazyWithRetries(() => import('~/src/Irrigation/Delete'))
const IrrigationScheduleDialogRoute = lazyWithRetries(() => import('~/src/Irrigation/Dialog').then(({
  IrrigationScheduleDialogRoute: isdRoute
}) => ({ default: isdRoute })))
const ImportTags = lazyWithRetries(() => import('~/src/Metrc/Tags/ImportTags'))
const NoteDelete = lazyWithRetries(() => import('~/src/Note/Delete'))
const NoteForm = lazyWithRetries(() => import('~/src/Note/Form'))
const NoteView = lazyWithRetries(() => import('~/src/Note/View'))
const BulkManualReadingForm = lazyWithRetries(() => import('~/src/Readings/ManualReading/BulkForm'))
const ManualReadingDelete = lazyWithRetries(() => import('~/src/Readings/ManualReading/Delete'))
const ManualReadingView = lazyWithRetries(() => import('~/src/Readings/ManualReading/View'))
const SubstrateSettingsDialog = lazyWithRetries(() => import('~/src/Room/SubstrateSettings'))
const LightScheduleDialog = lazyWithRetries(() => import('~/src/Room/LightSchedule/Dialog'))
const LightScheduleDelete = lazyWithRetries(() => import('~/src/Room/LightSchedule/Delete'))
const LightScheduleView = lazyWithRetries(() => import('~/src/Room/LightSchedule/View'))
const ConfigureDevice = lazyWithRetries(() => import('~/src/Setup/Devices/ConfigureDevice'))
const GradeDelete = lazyWithRetries(() => import('~/src/Setup/Grades/Delete'))
const GradeForm = lazyWithRetries(() => import('~/src/Setup/Grades/GradeForm'))
const ItemDelete = lazyWithRetries(() => import('~/src/Setup/Items/Delete'))
const ItemForm = lazyWithRetries(() => import('~/src/Setup/Items/ItemForm'))
const SupportForm = lazyWithRetries(() => import('~/src/Support/SupportForm'))
const TaskManager = lazyWithRetries(() => import('~/src/Task/TaskManager/Dialog/'))
const TaskDelete = lazyWithRetries(() => import('~/src/Task/Delete'))
const TaskReviewHours = lazyWithRetries(() => import('~/src/Task/ReviewHours'))
const TaskForm = lazyWithRetries(() => import('~/src/Task/TaskFormRoute'))
const TaskView = lazyWithRetries(() => import('~/src/Task/TaskViewRoute'))

const logger = createLogger('Dialog/routes')
const throttledWarn = throttle(logger.warn, 250)

export const SCHEMA_ACTION_TO_ROUTE = {
  devices: {
    default: {
      Component: DeviceActivation,
      actions: ['change_device'],
      features: ['SELF_PROVISIONING'],
    },
    search: {
      Component: DeviceSearch,
      actions: ['change_device'],
      features: ['SELF_PROVISIONING'],
    },
  },
  events: {
    resolve: {
      Component: ConfirmOptionDialog,
      autoFetch: false,
      actions: ['sync_processing', 'sync_cultivation', 'destroy_plant'],
      anyAction: true,
      features: 'METRC_COMMON_FIXES',
    }
  },
  facility: {
    edit: {
      Component: FacilityForm,
      actions: ['change_facility'],
    },
  },
  facilityCultivars: {
    new: CultivarsForm,
    edit: CultivarsForm,
  },
  flows: {
    discard: FlowDiscardRoute,
  },
  grades: {
    new: GradeForm,
    edit: GradeForm,
    deleting: GradeDelete,
  },
  harvests: {
    phaseAdvance: PhaseAdvance,
    phaseDelay: PhaseDelay,
    view: HarvestView,
    [HARVESTING_FLOW]: {
      autoFetch: false,
      Component: HarvestingFlow,
      actions: ['harvest_flow'],
    },
    [DRYING_FLOW]: ProcessingFlow,
    lot2plant: LotToPlant,
  },
  harvestBatches: {
    view: InventoryModal,
  },
  harvestPhases: {
    view: HarvestView,
  },
  irrigationControllers: {
    new: IrrigationControllerDialog,
    edit: IrrigationControllerDialog,
    configure: IrrigationControllerDialog,
    deleting: IrrigationControllerDelete,
    view: IrrigationControllerDialog,
    registers: ModbusRegistersDialog,
  },
  irrigationSchedules: {
    new: IrrigationScheduleDialogRoute,
    edit: {
      autoFetch: false,
      Component: IrrigationScheduleDialogRoute,
    },
    deleting: {
      autoFetch: false,
      Component: IrrigationScheduleDelete,
    },
  },
  items: {
    new: ItemForm,
    edit: ItemForm,
    deleting: ItemDelete,
  },
  lightSchedules: {
    new: LightScheduleDialog,
    view: LightScheduleView,
    edit: LightScheduleDialog,
    deleting: LightScheduleDelete
  },
  loggers: {
    edit: ConfigureDevice,
  },
  notes: {
    view: NoteView,
    new: NoteForm,
    edit: NoteForm,
    deleting: NoteDelete
  },
  packages: {
    view: InventoryModal,
    replaceInvalidTag: ReplaceInvalidTag
  },
  pesticideApplications: {
    view: PesticideApplicationView,
    new: PesticideApplicationForm,
    edit: PesticideApplicationForm,
    deleting: PesticideApplicationDelete,
  },
  pesticides: {
    new: PesticideForm,
    edit: PesticideForm,
    deleting: PesticideDelete,
  },
  plants: {
    view: InventoryModal,
    replaceInvalidTag: ReplaceInvalidTag
  },
  plantBatches: {
    view: InventoryModal,
    replaceInvalidTag: ReplaceInvalidTag
  },
  readings: {
    new: BulkManualReadingForm,
    view: ManualReadingView,
    edit: ManualReadingForm,
    deleting: ManualReadingDelete
  },
  [TARGET_RANGES]: {
    view: {
      Component: DayNightTargetRangeView,
      autoFetch: false,
      noEntity: true,
    },
    default: {
      Component: DayNightTargetRangesDialog,
      autoFetch: false,
      actions: ['change_targetrange'],
      noEntity: true,
    },
    deleting: TargetRangeDelete,
  },
  tasks: TASK_VIEW_TABS.reduce((taskRoutes, tabKey) => {
    taskRoutes[tabKey] = TaskView
    return taskRoutes
  }, {
    view: TaskView,
    new: TaskForm,
    edit: TaskForm,
    deleting: TaskDelete,
    duplicate: TaskForm,
    review: TaskReviewHours
  }),
  [REPLACE_PLANT]: {
    Component: ReplacePlant,
    flags: { features: ['METRC'] },
  },
  [TASK_EDITOR]: {
    Component: TaskManager,
    tiers: ['GO'],
    negate: ['tiers'],
  },
  [DRYING_FLOW]: {
    Component: ProcessingFlow,
    permissions: { actions: ['end_drying_flow'] },
    flags: { features: ['METRC'] },
    apps: 'AROYA',
  },
  [PROCESSING_FLOW]: {
    Component: ProcessingFlow,
    permissions: { actions: ['processing_flow'] },
    flags: { features: ['METRC'] },
    apps: 'AROYA',
  },
}

const LOT_2_PLANT_ROUTES = [
  { flow: LOT_TO_PLANT_FLOW, routes: [HARVEST_BASED_LOT_TO_PLANT_FLOW_ROUTE, LOT_TO_PLANT_FLOW_ROUTE] },
  { flow: VEG_TO_FLOWER_FLOW, routes: [HARVEST_BASED_VEG_TO_FLOWER_FLOW_ROUTE, VEG_TO_FLOWER_FLOW_ROUTE] },
]

const defaultLookupKwargs = Object.freeze({ defaultAction: 'view', useRouteInfo: false })
export const routeLookupHandler = (opts = defaultLookupKwargs) => {
  const {
    defaultAction,
    defaultSchema,
    additionalProps: defaultAdditionalProps,
    noEntity = false,
    path: optsPath,
    useRouteInfo,
  } = opts
  const Handler = () => {
    const {
      currents,
      dialogRouteInfo,
      routeInfo,
      doCloseDialogRoute,
    } = useConnect(
      'selectCurrents',
      'selectDialogRouteInfo',
      'selectRouteInfo',
      'doCloseDialogRoute',
    )
    const { params } = (useRouteInfo ? routeInfo : dialogRouteInfo) ?? EMPTY_OBJECT

    const [open, setOpen] = useState(true)
    const onClose = useCallback(() => {
      setOpen(false)
      doCloseDialogRoute()
    }, [doCloseDialogRoute])

    const onDelete = useCallback(() => {
      if (!params || params.action !== 'deleting') return
      defer(onClose, defer.priorities.low)
    }, [onClose, params])
    const defaultFormState = useDefaultAnnotationFormState({})

    if (!dialogRouteInfo) {
      logger.debug('no dialog route info:', dialogRouteInfo)
      return null
    }

    const {
      params: {
        id,
        action = defaultAction,
        schema = defaultSchema,
        date: dateRaw
      }
    } = dialogRouteInfo
    let date
    if (dateRaw) {
      date = getDateTime(Number(dateRaw))
      if (!date.isValid) {
        date = undefined
      }
    }

    let handler
    if (opts.Component) {
      handler = opts
    } else {
      const { [schema]: handlers = EMPTY_OBJECT } = SCHEMA_ACTION_TO_ROUTE
      if (handlers) {
        ({ [action]: handler } = handlers)
      }
    }

    if (handler && handler.autoFetch && !noEntity && action !== 'new' && ((!schema && !optsPath) || (id && !currents[schema]))) {
      throttledWarn('entity not set, aborting:', { id, schema, currents, opts })
      return null
    }

    const Component = handler ? (handler.Component ?? handler) : null

    let additionalProps = { ...defaultAdditionalProps }
    if (action === 'new') {
      additionalProps = { ...additionalProps, ...defaultFormState }
    }

    const finalProps = {
      ...additionalProps,
      action,
      schema,
      date,
      open,
      id: id && Number(id),
      onClose,
      onDelete: action === 'deleting' ? onDelete : undefined
    }

    let content = Component
      ? createElement(
        Component,
        finalProps
      )
      : null
    if (allowedProps.some(key => opts[key]) || (handler && allowedProps.some(key => handler[key]))) {
      const allowedPassthru = { ...pickAllowedProps(opts), ...pickAllowedProps(handler ?? {}) }
      logger.debug('route is protected:', allowedPassthru)
      content = createElement(
        Allowed,
        {
          Fallback: NotFound,
          FallbackProps: finalProps,
          PermissionFallback: Forbidden,
          ...allowedPassthru,
        },
        content
      )
    }
    return content
  }
  Object.assign(Handler, opts)
  return Handler
}

export default {
  '/devices': routeLookupHandler({
    defaultSchema: 'devices',
    defaultAction: 'default',
  }),
  [FOUND_DEVICES_DIALOG_URL]: routeLookupHandler({
    defaultSchema: 'devices',
    defaultAction: 'search',
  }),
  '/facilityEdit': {
    schema: 'facility',
    action: 'edit',
  },
  '/irrigationTemplates/new': routeLookupHandler({
    defaultAction: 'new',
    defaultSchema: 'irrigationSchedules',
  }),
  '/irrigationTemplates/:id/:action': routeLookupHandler({
    defaultSchema: 'irrigationSchedules',
  }),
  '/phaseAdvance': routeLookupHandler({
    defaultAction: 'phaseAdvance',
    defaultSchema: 'harvests',
    useRouteInfo: true,
    actions: ['change_harvest'],
    features: ['METRC'],
    apps: 'AROYA',
  }),
  '/phaseAdvance/:id': routeLookupHandler({
    defaultAction: 'phaseAdvance',
    defaultSchema: 'harvests',
    permissions: { actions: ['change_harvest'] },
    flags: { features: ['METRC'] },
    apps: 'AROYA',
  }),
  '/phaseDelay': routeLookupHandler({
    defaultAction: 'phaseDelay',
    defaultSchema: 'harvests',
    useRouteInfo: true,
    permissions: { actions: ['change_harvest'] },
    flags: { features: ['METRC'] },
    apps: 'AROYA',
  }),
  '/phaseDelay/:id/:previousSubId': routeLookupHandler({
    defaultAction: 'phaseDelay',
    defaultSchema: 'harvests',
    permissions: { actions: ['change_harvest'] },
    flags: { features: ['METRC'] },
    apps: 'AROYA',
  }),
  ...LOT_2_PLANT_ROUTES.reduce((l2pRoutes, { flow, routes }) => ({
    ...l2pRoutes,
    ...routes.reduce((flowRoutes, route) => ({
      ...flowRoutes,
      [route]: routeLookupHandler({
        defaultAction: 'lot2plant',
        defaultSchema: 'harvests',
        additionalProps: { flow },
        flags: { features: ['METRC'] },
        permissions: { actions: ['lot2plant_flow'] },
        apps: 'AROYA',
      }),
    }), EMPTY_OBJECT)
  }), EMPTY_OBJECT),
  '/support': SupportForm,
  '/tags': ImportTags,
  [HARVEST_BASED_HARVESTING_FLOW_ROUTE]: routeLookupHandler({
    defaultAction: HARVESTING_FLOW,
    defaultSchema: 'harvests',
    permissions: { actions: ['harvest_flow'] },
    apps: 'AROYA',
  }),
  [DRYING_FLOW_ROUTE]: {
    path: [DRYING_FLOW],
  },
  [HARVEST_BASED_DRYING_FLOW_ROUTE]: routeLookupHandler({
    defaultAction: DRYING_FLOW,
    defaultSchema: 'harvests',
    flags: { features: ['METRC'] },
    permissions: { actions: ['end_drying_flow'] },
    apps: 'AROYA',
  }),
  [PROCESSING_FLOW_ROUTE]: {
    path: [PROCESSING_FLOW],
  },
  [HARVEST_BASED_PROCESSING_FLOW_ROUTE]: ProcessingFlow,
  '/tasks/:id/completing': routeLookupHandler({
    defaultAction: 'view',
    defaultSchema: 'tasks',
    additionalProps: {
      completing: true
    },
    apps: 'AROYA',
  }),
  [`/${SUBTRATE_SETTINGS}`]: SubstrateSettingsDialog,
  [TASK_EDITOR_ROUTE]: {
    path: [TASK_EDITOR]
  },
  [`/${REPLACE_PLANT_ROUTE}`]: {
    path: [REPLACE_PLANT]
  },
  '/tasks/newIPM': routeLookupHandler({
    defaultAction: 'new',
    defaultSchema: 'tasks',
    additionalProps: {
      category: 'PEST',
    },
    apps: 'AROYA',
  }),
  [harvestUrls.veg2flower]: routeLookupHandler({
    defaultAction: 'veg2flower',
    defaultSchema: 'harvests',
    flags: { features: ['METRC'] },
    permissions: { actions: ['lot2plant_flow'] },
    apps: 'AROYA',
  }),
  '/:schema/:id/replaceInvalidTag': routeLookupHandler({
    defaultAction: 'replaceInvalidTag',
    flags: { features: ['METRC'] },
  }),
  '/:schema/new/:date': routeLookupHandler({ defaultAction: 'new' }),
  '/:schema/new': routeLookupHandler({ defaultAction: 'new' }),
  '/:schema/:id': routeLookupHandler(),
  '/:schema/:id/:action': routeLookupHandler(),
  '/:schema': routeLookupHandler({ defaultAction: 'default' }),
  '*': NotFound
}
